import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// Set to true for local testing without AWS, false for real Cognito auth
const USE_MOCK_AUTH = false;

export type UserRole = 'admin' | 'user';

export interface User {
  username: string;
  email: string;
  userId: string;
  role: UserRole;
  firstName?: string;
  lastName?: string;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  needsNewPassword: boolean;
  needsMfaCode: boolean;
  needsMfaSetup: boolean;
  needsMfaSelection: boolean;
  availableMfaTypes: string[];
  mfaType: 'TOTP' | 'SMS' | 'EMAIL' | null;
  totpSetupUri: string | null;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  confirmSignUp: (email: string, code: string) => Promise<void>;
  completeNewPassword: (newPassword: string) => Promise<void>;
  confirmMfaCode: (code: string) => Promise<void>;
  confirmTotpSetup: (code: string) => Promise<void>;
  selectMfaType: (type: string) => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  confirmForgotPassword: (email: string, code: string, newPassword: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Mock users for testing
const mockUsers: { [email: string]: { password: string; userId: string; role: UserRole } } = {
  // Admin users
  'admin@silverscribe.ca': { password: 'Admin1234!', userId: 'admin-001', role: 'admin' },
  'superadmin@silverscribe.ca': { password: 'Super1234!', userId: 'admin-002', role: 'admin' },
  // Regular users
  'user@silverscribe.ca': { password: 'User1234!', userId: 'user-001', role: 'user' },
  'demo@silverscribe.ca': { password: 'Demo1234!', userId: 'user-002', role: 'user' },
  'test@silverscribe.ca': { password: 'Test1234!', userId: 'user-003', role: 'user' },
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [needsNewPassword, setNeedsNewPassword] = useState(false);
  const [needsMfaCode, setNeedsMfaCode] = useState(false);
  const [needsMfaSetup, setNeedsMfaSetup] = useState(false);
  const [mfaType, setMfaType] = useState<'TOTP' | 'SMS' | 'EMAIL' | null>(null);
  const [totpSetupUri, setTotpSetupUri] = useState<string | null>(null);
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [needsMfaSelection, setNeedsMfaSelection] = useState(false);
  const [availableMfaTypes, setAvailableMfaTypes] = useState<string[]>([]);

  useEffect(() => {
    checkAuthState();
  }, []);

  const checkAuthState = async () => {
    if (USE_MOCK_AUTH) {
      // Check localStorage for mock session
      const savedUser = localStorage.getItem('mockUser');
      if (savedUser) {
        setUser(JSON.parse(savedUser));
      }
      setIsLoading(false);
    } else {
      // Real Cognito auth check
      try {
        const { getCurrentUser, fetchAuthSession } = await import('aws-amplify/auth');
        const currentUser = await getCurrentUser();
        const session = await fetchAuthSession();

        // Get groups from the ID token
        const idToken = session.tokens?.idToken;
        const groups = idToken?.payload['cognito:groups'] as string[] || [];
        const isAdmin = groups.includes('admins');

        setUser({
          username: currentUser.username,
          email: idToken?.payload['email'] as string || '',
          userId: currentUser.userId,
          role: isAdmin ? 'admin' : 'user',
          firstName: idToken?.payload['given_name'] as string || undefined,
          lastName: idToken?.payload['family_name'] as string || undefined,
        });
      } catch {
        setUser(null);
      }
      setIsLoading(false);
    }
  };

  const signIn = async (email: string, password: string) => {
    if (USE_MOCK_AUTH) {
      // Mock sign in
      const mockUser = mockUsers[email.toLowerCase()];
      if (mockUser && mockUser.password === password) {
        const user: User = {
          username: email.split('@')[0],
          email: email.toLowerCase(),
          userId: mockUser.userId,
          role: mockUser.role,
        };
        setUser(user);
        localStorage.setItem('mockUser', JSON.stringify(user));
      } else {
        throw new Error('Invalid email or password');
      }
    } else {
      // Don't start a new sign-in if one is already in progress
      if (isSigningIn) {
        throw new Error('Sign in already in progress');
      }

      // Real Cognito sign in
      const { signIn, signOut } = await import('aws-amplify/auth');

      setIsSigningIn(true);

      const handleSignInResult = (result: any) => {
        console.log('[Auth] signIn step:', result.nextStep?.signInStep, result);
        if (result.isSignedIn) {
          setIsSigningIn(false);
          checkAuthState();
        } else if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
          setNeedsNewPassword(true);
        } else if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
          setMfaType('TOTP');
          setNeedsMfaCode(true);
        } else if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_SMS_CODE') {
          setMfaType('SMS');
          setNeedsMfaCode(true);
        } else if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE') {
          setMfaType('EMAIL');
          setNeedsMfaCode(true);
        } else if (result.nextStep?.signInStep === 'CONTINUE_SIGN_IN_WITH_MFA_SELECTION') {
          const types = result.nextStep.allowedMFATypes || [];
          setAvailableMfaTypes(types);
          setNeedsMfaSelection(true);
        } else if (result.nextStep?.signInStep === 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP') {
          const setupDetails = result.nextStep.totpSetupDetails;
          const uri = setupDetails?.getSetupUri('SilverScribe').toString() || null;
          setTotpSetupUri(uri);
          setNeedsMfaSetup(true);
        } else {
          console.warn('[Auth] Unhandled signIn step:', result.nextStep?.signInStep);
          setIsSigningIn(false);
        }
      };

      const attemptSignIn = async () => {
        const result = await signIn({ username: email, password });
        handleSignInResult(result);
      };

      try {
        await attemptSignIn();
      } catch (error: any) {
        // If there's already a signed in user, sign out and retry
        if (error.message?.includes('already a signed in user')) {
          await signOut();
          await attemptSignIn();
        } else {
          setIsSigningIn(false);
          throw error;
        }
      }
    }
  };

  const completeNewPassword = async (newPassword: string) => {
    if (USE_MOCK_AUTH) {
      setNeedsNewPassword(false);
      setIsSigningIn(false);
    } else {
      try {
        const { confirmSignIn } = await import('aws-amplify/auth');
        const result = await confirmSignIn({ challengeResponse: newPassword });
        if (result.isSignedIn) {
          setNeedsNewPassword(false);
          setIsSigningIn(false);
          await checkAuthState();
        } else if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
          setNeedsNewPassword(false);
          setMfaType('TOTP');
          setNeedsMfaCode(true);
        } else if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_SMS_CODE') {
          setNeedsNewPassword(false);
          setMfaType('SMS');
          setNeedsMfaCode(true);
        } else if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE') {
          setNeedsNewPassword(false);
          setMfaType('EMAIL');
          setNeedsMfaCode(true);
        } else if (result.nextStep?.signInStep === 'CONTINUE_SIGN_IN_WITH_MFA_SELECTION') {
          setNeedsNewPassword(false);
          const types = result.nextStep.allowedMFATypes || [];
          setAvailableMfaTypes(types);
          setNeedsMfaSelection(true);
        } else if (result.nextStep?.signInStep === 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP') {
          setNeedsNewPassword(false);
          const setupDetails = result.nextStep.totpSetupDetails;
          const uri = setupDetails?.getSetupUri('SilverScribe').toString() || null;
          setTotpSetupUri(uri);
          setNeedsMfaSetup(true);
        }
      } catch (error) {
        setIsSigningIn(false);
        throw error;
      }
    }
  };

  const confirmMfaCode = async (code: string) => {
    if (USE_MOCK_AUTH) {
      setNeedsMfaCode(false);
      setMfaType(null);
      setIsSigningIn(false);
    } else {
      try {
        const { confirmSignIn, getCurrentUser, fetchAuthSession } = await import('aws-amplify/auth');
        const result = await confirmSignIn({ challengeResponse: code });

        if (result.isSignedIn) {
          const currentUser = await getCurrentUser();
          const session = await fetchAuthSession();
          const idToken = session.tokens?.idToken;
          const groups = idToken?.payload['cognito:groups'] as string[] || [];
          const isAdmin = groups.includes('admins');

          setUser({
            username: currentUser.username,
            email: idToken?.payload['email'] as string || '',
            userId: currentUser.userId,
            role: isAdmin ? 'admin' : 'user',
            firstName: idToken?.payload['given_name'] as string || undefined,
            lastName: idToken?.payload['family_name'] as string || undefined,
          });
          setNeedsMfaCode(false);
          setMfaType(null);
          setIsSigningIn(false);
        } else {
          throw new Error('Sign in was not completed');
        }
      } catch (error: any) {
        // Check if user is already signed in (first click worked)
        try {
          const { getCurrentUser } = await import('aws-amplify/auth');
          await getCurrentUser();
          await checkAuthState();
          setNeedsMfaCode(false);
          setMfaType(null);
          setIsSigningIn(false);
          return;
        } catch {
          // Not signed in, rethrow original error
        }
        setNeedsMfaCode(false);
        setMfaType(null);
        setIsSigningIn(false);
        throw error;
      }
    }
  };

  const confirmTotpSetup = async (code: string) => {
    if (USE_MOCK_AUTH) {
      setNeedsMfaSetup(false);
      setTotpSetupUri(null);
      setIsSigningIn(false);
    } else {
      try {
        const { confirmSignIn, getCurrentUser, fetchAuthSession } = await import('aws-amplify/auth');
        const result = await confirmSignIn({ challengeResponse: code });

        if (result.isSignedIn) {
          // Fetch user info directly
          const currentUser = await getCurrentUser();
          const session = await fetchAuthSession();
          const idToken = session.tokens?.idToken;
          const groups = idToken?.payload['cognito:groups'] as string[] || [];
          const isAdmin = groups.includes('admins');

          setUser({
            username: currentUser.username,
            email: idToken?.payload['email'] as string || '',
            userId: currentUser.userId,
            role: isAdmin ? 'admin' : 'user',
            firstName: idToken?.payload['given_name'] as string || undefined,
            lastName: idToken?.payload['family_name'] as string || undefined,
          });
          setNeedsMfaSetup(false);
          setTotpSetupUri(null);
          setIsSigningIn(false);
        } else {
          throw new Error('Sign in was not completed');
        }
      } catch (error: any) {
        // Check if user is already signed in
        try {
          const { getCurrentUser } = await import('aws-amplify/auth');
          await getCurrentUser();
          await checkAuthState();
          setNeedsMfaSetup(false);
          setTotpSetupUri(null);
          setIsSigningIn(false);
          return;
        } catch {
          // Not signed in, rethrow original error
        }
        setNeedsMfaSetup(false);
        setTotpSetupUri(null);
        setIsSigningIn(false);
        throw error;
      }
    }
  };

  const selectMfaType = async (type: string) => {
    try {
      const { confirmSignIn, getCurrentUser, fetchAuthSession } = await import('aws-amplify/auth');
      const result = await confirmSignIn({ challengeResponse: type });
      console.log('[Auth] selectMfaType step:', result.nextStep?.signInStep, result);

      setNeedsMfaSelection(false);
      setAvailableMfaTypes([]);

      if (result.isSignedIn) {
        const currentUser = await getCurrentUser();
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken;
        const groups = idToken?.payload['cognito:groups'] as string[] || [];
        const isAdmin = groups.includes('admins');

        setUser({
          username: currentUser.username,
          email: idToken?.payload['email'] as string || '',
          userId: currentUser.userId,
          role: isAdmin ? 'admin' : 'user',
          firstName: idToken?.payload['given_name'] as string || undefined,
          lastName: idToken?.payload['family_name'] as string || undefined,
        });
        setIsSigningIn(false);
      } else if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
        setMfaType('TOTP');
        setNeedsMfaCode(true);
      } else if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_SMS_CODE') {
        setMfaType('SMS');
        setNeedsMfaCode(true);
      } else if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE') {
        setMfaType('EMAIL');
        setNeedsMfaCode(true);
      } else if (result.nextStep?.signInStep === 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP') {
        const setupDetails = result.nextStep.totpSetupDetails;
        const uri = setupDetails?.getSetupUri('SilverScribe').toString() || null;
        setTotpSetupUri(uri);
        setNeedsMfaSetup(true);
      }
    } catch (error) {
      setNeedsMfaSelection(false);
      setAvailableMfaTypes([]);
      setIsSigningIn(false);
      throw error;
    }
  };

  const signUp = async (email: string, password: string) => {
    if (USE_MOCK_AUTH) {
      // Mock sign up - add to mock users as regular user
      mockUsers[email.toLowerCase()] = {
        password,
        userId: `user-${Date.now()}`,
        role: 'user',
      };
      console.log('Mock user created. Use confirmation code: 123456');
    } else {
      // Real Cognito sign up
      const { signUp } = await import('aws-amplify/auth');
      await signUp({
        username: email,
        password,
        options: {
          userAttributes: { email },
        },
      });
    }
  };

  const confirmSignUp = async (email: string, code: string) => {
    if (USE_MOCK_AUTH) {
      // Mock confirmation - accept any code for testing
      if (code === '123456') {
        console.log('Mock user confirmed');
      } else {
        throw new Error('Invalid confirmation code. Use: 123456');
      }
    } else {
      // Real Cognito confirmation
      const { confirmSignUp } = await import('aws-amplify/auth');
      await confirmSignUp({ username: email, confirmationCode: code });
    }
  };

  const signOut = async () => {
    if (USE_MOCK_AUTH) {
      setUser(null);
      localStorage.removeItem('mockUser');
    } else {
      const { signOut } = await import('aws-amplify/auth');
      await signOut();
      setUser(null);
      setIsSigningIn(false);
      setNeedsNewPassword(false);
      setNeedsMfaCode(false);
      setNeedsMfaSetup(false);
      setNeedsMfaSelection(false);
      setAvailableMfaTypes([]);
      setMfaType(null);
      setTotpSetupUri(null);
    }
  };

  const forgotPassword = async (email: string) => {
    if (USE_MOCK_AUTH) {
      console.log('Mock password reset requested. Use code: 123456');
    } else {
      const { resetPassword } = await import('aws-amplify/auth');
      await resetPassword({ username: email });
    }
  };

  const confirmForgotPassword = async (email: string, code: string, newPassword: string) => {
    if (USE_MOCK_AUTH) {
      if (code === '123456') {
        console.log('Mock password reset confirmed');
      } else {
        throw new Error('Invalid code. Use: 123456');
      }
    } else {
      const { confirmResetPassword } = await import('aws-amplify/auth');
      await confirmResetPassword({ username: email, confirmationCode: code, newPassword });
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        isAdmin: user?.role === 'admin',
        needsNewPassword,
        needsMfaCode,
        needsMfaSetup,
        needsMfaSelection,
        availableMfaTypes,
        mfaType,
        totpSetupUri,
        signIn,
        signUp,
        signOut,
        confirmSignUp,
        completeNewPassword,
        confirmMfaCode,
        confirmTotpSetup,
        selectMfaType,
        forgotPassword,
        confirmForgotPassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
